import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  LucideArrowLeftRight,
  LucideBuilding2,
  LucideLifeBuoy,
  LucideLogOut,
  LucideSettings,
  LucideUserCircle,
} from '@lucide/angular';

import { AccountAdminService } from '../../../core/auth/account-admin.service';
import { AuthService } from '../../../core/auth/auth.service';
import { ProfileService } from '../../../core/profile/profile.service';

@Component({
  selector: 'g-top-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sticky top-0 z-40',
  },
  imports: [
    RouterLink,
    RouterLinkActive,
    LucideArrowLeftRight,
    LucideBuilding2,
    LucideLifeBuoy,
    LucideLogOut,
    LucideSettings,
    LucideUserCircle,
  ],
  template: `
    <header class="border-b border-border-subtle bg-surface-base px-4 sm:px-6">
      <div class="flex h-14 items-center justify-between gap-4 max-w-7xl mx-auto w-full">
        <div class="flex items-center gap-6">
          <a
            routerLink="/"
            class="inline-flex flex-shrink-0 items-center gap-2"
            aria-label="Growzz home"
          >
            <img
              src="assets/img/logo-mark.svg"
              alt=""
              aria-hidden="true"
              class="h-7 w-auto sm:hidden"
            />
            <img
              src="assets/img/logo-full.svg"
              alt="Growzz"
              class="hidden h-auto w-[130px] sm:block"
            />
          </a>

          <nav class="flex items-center gap-1" aria-label="Primary">
            <a
              routerLink="/"
              routerLinkActive="bg-surface-sunken text-text-primary"
              [routerLinkActiveOptions]="{ exact: true }"
              class="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken hover:text-text-primary"
              >Home</a
            >
            <a
              routerLink="/plans"
              routerLinkActive="bg-surface-sunken text-text-primary"
              class="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken hover:text-text-primary"
              >Plans</a
            >
          </nav>
        </div>

        <div class="relative">
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-full px-2 py-1 text-sm text-text-primary hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
            [attr.aria-expanded]="menuOpen()"
            aria-haspopup="menu"
            (click)="toggleMenu($event)"
          >
            <span
              class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-100 text-brand-700 text-xs font-semibold"
              aria-hidden="true"
            >
              {{ initials() }}
            </span>
            <span class="hidden sm:inline">{{ displayName() }}</span>
          </button>
          @if (menuOpen()) {
            <div
              class="absolute right-0 mt-2 w-56 rounded-lg border border-border-subtle bg-surface-base shadow-md py-1"
              role="menu"
            >
              <div class="px-3 pt-2 pb-1.5">
                @if (activeAccountName(); as accountName) {
                  <p class="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                    <svg
                      lucideBuilding2
                      size="14"
                      aria-hidden="true"
                      class="shrink-0 text-text-muted"
                    ></svg>
                    <span class="truncate">{{ accountName }}</span>
                  </p>
                }
                <p class="text-xs text-text-muted truncate">
                  {{ auth.email() ?? 'Signed in' }}
                </p>
              </div>
              @if (isAccountAdmin()) {
                <a
                  routerLink="/admin/account/users"
                  role="menuitem"
                  class="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-surface-sunken"
                  (click)="menuOpen.set(false)"
                >
                  <svg lucideSettings size="14" aria-hidden="true"></svg>
                  Account admin
                </a>
              }
              @if (hasMultipleAccounts()) {
                <a
                  routerLink="/account/switch"
                  role="menuitem"
                  class="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-surface-sunken"
                  (click)="menuOpen.set(false)"
                >
                  <svg lucideArrowLeftRight size="14" aria-hidden="true"></svg>
                  Change account
                </a>
              }
              <div class="my-1 border-t border-border-subtle" role="separator"></div>
              <a
                routerLink="/me"
                role="menuitem"
                class="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-surface-sunken"
                (click)="menuOpen.set(false)"
              >
                <svg lucideUserCircle size="14" aria-hidden="true"></svg>
                Personal settings
              </a>
              <button
                type="button"
                id="help_link"
                role="menuitem"
                class="flex w-full items-center gap-2 text-left px-3 py-2 text-sm hover:bg-surface-sunken"
              >
                <svg lucideLifeBuoy size="14" aria-hidden="true"></svg>
                Message support
              </button>
              <button
                type="button"
                role="menuitem"
                class="flex w-full items-center gap-2 text-left px-3 py-2 text-sm hover:bg-surface-sunken"
                (click)="logout()"
              >
                <svg lucideLogOut size="14" aria-hidden="true"></svg>
                Log out
              </button>
            </div>
          }
        </div>
      </div>
    </header>
  `,
})
export class TopNavComponent implements OnInit {
  protected readonly auth = inject(AuthService);
  private readonly profile = inject(ProfileService);
  private readonly accountAdmin = inject(AccountAdminService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly router = inject(Router);

  protected readonly menuOpen = signal(false);
  protected readonly isAccountAdmin = this.accountAdmin.isAccountAdmin;
  protected readonly hasMultipleAccounts = this.auth.hasMultipleAccounts;
  protected readonly activeAccountName = computed(() => this.auth.activeAccount()?.name ?? null);

  /**
   * The Growzz profile is the source of truth (set by the onboarding guard
   * before any authed route renders). Fall through to email only if the
   * profile somehow isn't loaded — that should be rare.
   */
  protected readonly displayName = computed(() => {
    const user = this.profile.profile();
    const fullName = [user?.firstName, user?.lastName]
      .map((part) => part?.trim())
      .filter((part): part is string => !!part)
      .join(' ');
    if (fullName) return fullName;
    return this.auth.email() ?? 'You';
  });

  async ngOnInit(): Promise<void> {
    // `accountAdmin.ensureLoaded()` resolves the active account itself, so these
    // two independent `/users/me` fetches (memberships + accounts) run in
    // parallel. `ensureAccountsLoaded()` backs the account label and the
    // "Change account" entry. Best-effort: on failure those simply stay hidden.
    try {
      await Promise.all([this.accountAdmin.ensureLoaded(), this.auth.ensureAccountsLoaded()]);
    } catch {
      // Ignore — degraded nav (no account label / switcher entry) beats a throw.
    }
  }

  protected readonly initials = computed(() => {
    const user = this.profile.profile();
    const first = user?.firstName?.trim()?.[0];
    const last = user?.lastName?.trim()?.[0];
    if (first && last) return (first + last).toUpperCase();
    if (first) return first.toUpperCase();
    const email = this.auth.email();
    return email ? email[0]!.toUpperCase() : 'G';
  });

  protected toggleMenu(event: Event): void {
    event.stopPropagation();
    this.menuOpen.update((v) => !v);
  }

  protected logout(): void {
    // Navigate to the dedicated /logout screen (outside the app shell) rather
    // than clearing the session here. That tears down the nav and any account
    // screen first, so the user sees the full-screen "Signing you out…" state
    // instead of a flash of stale/cleared state (e.g. the account switcher's
    // "No accounts yet") while the Auth0 redirect is being set up. The
    // LogoutComponent performs the actual `auth.logout()`.
    this.menuOpen.set(false);
    void this.router.navigateByUrl('/logout');
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen()) return;
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      this.menuOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.menuOpen.set(false);
  }
}
